import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { AppState } from 'redux/reducer'
import { CountryCode } from 'interfaces/Country'
import { parseLocale } from 'lib/constants/localization'
import { User } from 'interfaces/User'
import { Language } from 'interfaces/Language'
import { UAParser } from 'ua-parser-js'

export interface Attributes {
    user: User | null
    countryCode: CountryCode
    contentLanguageCode: Language
    interfaceLanguageCode: Language
    deviceOS: 'android' | 'ios' | 'other'
    deviceFormFactor: 'mobile' | 'desktop' | 'tablet'
}

const useWebConfigAttributes = (): Attributes | null => {
    const [userAttributes, setUserAttributes] = useState<Attributes | null>(null)
    const authenticatedUser = useSelector((state: AppState) => state.user)
    const router = useRouter()
    const parsedLocale = parseLocale(router.locale)
    const parser = UAParser()

    const returnDeviceOS = (): Attributes['deviceOS'] => {
        switch (parser.os.name?.toLocaleLowerCase()) {
            case 'android':
                return 'android'
            case 'ios':
                return 'ios'
            default:
                return 'other'
        }
    }

    const returnDeviceFormFactor = (): Attributes['deviceFormFactor'] => {
        switch (parser.device.type) {
            case 'mobile':
                return 'mobile'
            case 'tablet':
                return 'tablet'
            default:
                return 'desktop'
        }
    }

    useEffect(() => {
        const attributes: Attributes = {
            user: authenticatedUser,
            countryCode: parsedLocale.countryCode,
            contentLanguageCode: parsedLocale.contentLanguageCode,
            interfaceLanguageCode: parsedLocale.interfaceLanguageCode,
            deviceOS: returnDeviceOS(),
            deviceFormFactor: returnDeviceFormFactor(),
        }

        setUserAttributes(attributes)
    }, [])

    return userAttributes
}

export default useWebConfigAttributes
